import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Chip,
  Box,
  Typography,
} from "@mui/material";
import {
  X,
  MessageSquare,
  Phone,
  Send,
  Loader2,
  Users,
} from "lucide-react";
import { DealsRequest } from "../../requests/vendor/DealsRequest";
import { toast } from "react-toastify";
import Config from "../../Config";

const TestSmsModal = ({
  open,
  onClose,
  testData,
  onConfirm,
  setIsTestSmsModalOpen,
}) => {
  const { phoneNumbers, previewMessage, campaignSlug } = testData;
  const [isLoading, setIsLoading] = React.useState(false);

  //Todo => All vendors use Dealboxx as sender ID
  const senderName = "Dealboxx";

  const handleTestSmsConfirm = async () => {
    try {
      const phone_numbers = phoneNumbers.join(",");
      const data = {
        msisdns: phone_numbers,
        sender_id: senderName,
      };
      setIsLoading(true);
      const response = await DealsRequest.testDealBlast(data, campaignSlug);
      if (
        response?.data?.status_code === "000" &&
        response?.data?.in_error === false
      ) {
        setIsLoading(false);
        toast.success("Test SMS sent successfully");
        setIsTestSmsModalOpen(false);
        if (onConfirm) onConfirm();
      } else {
        setIsLoading(false);
        toast.error(response?.data?.reason);
      }
    } catch (error) {
      setIsLoading(false);
      toast.error(
        error?.response?.data?.reason ||
          "An error occurred while sending the test SMS"
      );
    } finally {
      setIsLoading(false);
      setIsTestSmsModalOpen(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "24px",
          padding: "0",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header with gradient background */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)",
          padding: "24px 32px",
          color: "white",
        }}
      >
        <DialogTitle sx={{ padding: 0 }}>
          <div className="flex justify-between items-center">
            <div className="flex gap-4 items-center">
              <div className="p-3 rounded-xl backdrop-blur-sm bg-white/20">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <div>
                <Typography variant="h5" className="mb-1 font-bold text-white">
                  Test SMS Campaign
                </Typography>
                <Typography variant="body2" className="text-white/80">
                  Send a test message before launching your campaign
                </Typography>
              </div>
            </div>
            <IconButton
              onClick={onClose}
              sx={{
                color: "white",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <X className="w-5 h-5" />
            </IconButton>
          </div>
        </DialogTitle>
      </Box>

      <DialogContent sx={{ padding: "32px", backgroundColor: "#fafafa" }}>
        <div className="space-y-6">
          {/* Main Content - Side by Side Layout */}
          <div className="grid grid-cols-1 gap-8 items-start lg:grid-cols-2">
            {/* Left Column - Recipients */}
            <div className="space-y-6">
              {/* Recipients Card */}
              <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="flex gap-3 justify-between items-center mb-4">
                  <div className="flex gap-3 items-center">
                    <div className="p-2 bg-green-50 rounded-lg">
                      <Users className="w-5 h-5 text-green-600" />
                    </div>
                    <Typography
                      variant="h6"
                      className="font-semibold text-gray-800"
                    >
                      Test Recipients
                    </Typography>
                  </div>
                  <Chip
                    label={`${phoneNumbers.length} recipient${
                      phoneNumbers.length > 1 ? "s" : ""
                    }`}
                    variant="outlined"
                    size="small"
                    sx={{ borderColor: "#22C55E", color: "#22C55E" }}
                  />
                </div>

                <div className="grid overflow-y-auto gap-3 max-h-64">
                  {phoneNumbers.map((phone, index) => (
                    <div
                      key={index}
                      className="flex gap-3 items-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100 transition-shadow hover:shadow-sm"
                    >
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Phone className="w-4 h-4 text-green-600" />
                      </div>
                      <Typography
                        variant="body1"
                        className="font-medium text-green-800"
                      >
                        {phone}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Phone Preview */}
            <div className="flex justify-center lg:justify-end">
              {/* Message Preview Card */}
              <div className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="flex gap-3 items-center mb-4">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <Send className="w-5 h-5 text-purple-600" />
                  </div>
                  <Typography
                    variant="h6"
                    className="font-semibold text-gray-800"
                  >
                    Message Preview
                  </Typography>
                </div>

                {/* Phone Mockup */}
                <div className="flex justify-center">
                  <div className="relative">
                    {/* Phone Frame */}
                    <div className="p-2 w-80 h-[410px] bg-gray-900 rounded-3xl shadow-2xl">
                      {/* Phone Screen */}
                      <div className="overflow-hidden relative w-full h-full bg-white rounded-2xl">
                        {/* Status Bar */}
                        <div className="flex justify-between items-center px-4 py-2 text-xs font-medium text-gray-700 bg-gray-50">
                          <span>9:41 AM</span>
                          <div className="flex gap-1 items-center">
                            <div className="flex gap-1">
                              <div className="w-1 h-1 bg-gray-700 rounded-full"></div>
                              <div className="w-1 h-1 bg-gray-700 rounded-full"></div>
                              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                            </div>
                            <span className="ml-1">100%</span>
                          </div>
                        </div>

                        {/* Messages Header */}
                        <div className="flex gap-3 items-center px-4 py-3 bg-white border-b border-gray-200">
                          <div className="flex justify-center items-center w-8 h-8 bg-green-500 rounded-full">
                            <MessageSquare className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1">
                            <Typography
                              variant="subtitle2"
                              className="font-semibold text-gray-900"
                            >
                              {senderName}
                            </Typography>
                            <Typography
                              variant="caption"
                              className="text-gray-500"
                            >
                              now
                            </Typography>
                          </div>
                        </div>

                        {/* Messages Area */}
                        <div className="flex-1 p-4 space-y-4 bg-gray-50">
                          {/* Actual Message Bubble */}
                          <div className="flex justify-start">
                            <div className="max-w-xs">
                              {/* Link Preview */}
                              <div className="p-3 mt-2 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex gap-2 items-center mb-1">
                                  <div className="w-4 h-4 bg-blue-500 rounded"></div>
                                  <Typography
                                    variant="caption"
                                    className="font-medium text-blue-700"
                                  >
                                    Dealboxx
                                  </Typography>
                                </div>
                                <div className="py-1">
                                  <Typography
                                    variant="body2"
                                    className="leading-relaxed text-gray-800"
                                  >
                                    {previewMessage}
                                  </Typography>
                                </div>
                                <Typography
                                  variant="caption"
                                  className="text-blue-600 break-all"
                                >
                                  {`${Config.consumerDomain}/product/${campaignSlug}`}
                                </Typography>
                              </div>
                              <Typography
                                variant="caption"
                                className="block mt-1 text-gray-500"
                              >
                                Just now
                              </Typography>
                            </div>
                          </div>

                          {/* Typing Indicator */}
                          <div className="flex justify-start">
                            <div className="px-4 py-3 bg-white rounded-2xl rounded-bl-md border border-gray-200 shadow-sm">
                              <div className="flex gap-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                <div
                                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                  style={{ animationDelay: "0.1s" }}
                                ></div>
                                <div
                                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                  style={{ animationDelay: "0.2s" }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Message Input Area */}
                        <div className="p-3 bg-white border-t border-gray-200">
                          <div className="flex gap-2 items-center">
                            <div className="flex-1 px-4 py-2 bg-gray-100 rounded-full">
                              <Typography
                                variant="body2"
                                className="text-gray-500"
                              >
                                Message
                              </Typography>
                            </div>
                            <div className="flex justify-center items-center w-8 h-8 bg-blue-500 rounded-full">
                              <Send className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Phone Reflection */}
                    <div className="absolute inset-0 bg-gradient-to-br to-transparent rounded-3xl pointer-events-none from-white/20"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      <DialogActions
        sx={{ padding: "24px 32px", backgroundColor: "white", gap: 2 }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size="large"
          sx={{
            borderColor: "#E5E7EB",
            color: "#374151",
            padding: "12px 24px",
            borderRadius: "12px",
            fontWeight: "600",
            "&:hover": {
              borderColor: "#D1D5DB",
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleTestSmsConfirm}
          variant="contained"
          size="large"
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )
          }
          sx={{
            background: "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)",
            padding: "12px 24px",
            borderRadius: "12px",
            fontWeight: "600",
            boxShadow: "0 4px 14px 0 rgba(34, 197, 94, 0.25)",
            whiteSpace: "nowrap",
            minWidth: "fit-content",
            "&:hover": {
              background: "linear-gradient(135deg, #16A34A 0%, #15803D 100%)",
              boxShadow: "0 6px 20px 0 rgba(34, 197, 94, 0.35)",
            },
            "&.Mui-disabled": {
              background: "#E5E7EB",
              color: "#9CA3AF",
            },
            "@media (max-width: 640px)": {
              padding: "12px 16px",
              fontSize: "14px",
            },
          }}
        >
          {isLoading ? "Sending..." : "Send Test"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestSmsModal;
